import React, { Fragment, useMemo, useCallback, useState, useEffect } from 'react';

import { createUseStyles, Drawer, Loader, Modal } from '@Sage/hoi-poi-ui';
import { cleanEmptyValues, isEqual } from '@Sage/ssm-utils';
import PropTypes from 'prop-types';

import { useWeb5Context } from 'lib/Web5Context';
import { intercomHideAndShow } from 'utils/custom';

import DrawerContent from './DrawerContent';
import DrawerHeader from './DrawerHeader';
import styles from './styles/index';
const name = 'HyperDrawer';
const useStyles = createUseStyles(styles, name);
const SIZES = {
    medium: 500,
    large: 750,
    big: 1000,
};

function HyperDrawer({
    children,
    isOpen,
    locales,
    isSaving,
    isSaveDisabled,
    onCancel,
    onSave,
    onSaveCreate,
    className,
    contentClassName,
    withoutHeader,
    withoutDrawerContent,
    onTransitionEnds,
    originalData,
    data,
    insideCrud,

    isLoading = false,
    stackIndex = 1,
    size = 'medium',
}) {
    const classes = useStyles();
    const { t } = useWeb5Context();

    const width = SIZES[size] || SIZES['medium'];
    const offset = width * ((stackIndex - 1) * 0.5);

    const translate = offset;
    const contentStyles = useMemo(
        () => ({
            transform: `translateX(-${translate}px)`,
        }),
        [translate],
    );
    const [cancelModal, setCancelModal] = useState({ isOpen: false });

    const generalStyle = useMemo(() => {
        let styles = {};
        if (stackIndex > 1) styles.overlay = { background: 'transparent' };
        return styles;
    }, [stackIndex]);

    const checkEquals = useCallback(
        (originalData, data) => {
            const finalData = cleanEmptyValues(data);
            const finalOriginalData = cleanEmptyValues(originalData);
            if (originalData && data && !isEqual(finalOriginalData, finalData)) {
                return setCancelModal({ isOpen: true });
            } else {
                onCancel && onCancel();
            }
        },
        [onCancel],
    );

    const onKeyPress = useCallback(
        (e) => {
            if (e.key === 'Escape' && (originalData || data)) {
                checkEquals(originalData, data);
            } else return;
        },
        [originalData, data, checkEquals],
    );

    useEffect(() => {
        if (!insideCrud)
            if (isOpen) {
                intercomHideAndShow('hide');
            } else {
                intercomHideAndShow('show');
            }
    }, [isOpen, insideCrud]);

    useEffect(() => {
        document.addEventListener('keydown', onKeyPress, false);
        return () => document.removeEventListener('keydown', onKeyPress, false);
    }, [onKeyPress]);

    const onRequestClose = useCallback(() => setCancelModal({ isOpen: false }), []);

    const onConfirmCancel = useCallback(
        () => checkEquals(originalData, data),
        [checkEquals, originalData, data],
    );

    const onConfirmClose = useCallback(() => {
        setCancelModal({ isOpen: false });
        onCancel && onCancel();
    }, [onCancel]);

    return (
        <Fragment>
            <Drawer
                onTransitionEnds={onTransitionEnds}
                isOpen={isOpen}
                style={generalStyle}
                contentStyles={stackIndex > 1 ? contentStyles : null}
                className={className}
                width={width}
            >
                {!isLoading && (
                    <Fragment>
                        {!withoutHeader && (
                            <DrawerHeader
                                locales={locales}
                                isSaving={isSaving}
                                isSaveDisabled={isSaveDisabled}
                                onCancel={onConfirmCancel}
                                onSave={onSave}
                                onSaveCreate={onSaveCreate}
                                onKeyPress={onKeyPress}
                            />
                        )}
                        {!withoutDrawerContent ? (
                            <DrawerContent className={contentClassName}>{children}</DrawerContent>
                        ) : (
                            children
                        )}
                    </Fragment>
                )}
                {isLoading && (
                    <div className={classes.loader}>
                        <Loader size="massive" />
                    </div>
                )}
            </Drawer>
            <Modal
                isOpen={cancelModal.isOpen}
                onConfirm={onConfirmClose}
                size="small"
                useCornerClose={false}
                onCancel={onRequestClose}
                title={t('cfm_label_discard_unsaved_changes')}
                confirmText={t('cfm_action_discard_changes')}
                cancelText={t('cfm_action_cancel')}
            />
        </Fragment>
    );
}

HyperDrawer.propTypes = {
    isLoading: PropTypes.bool,
    isOpen: PropTypes.bool,
    locales: PropTypes.object,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    withoutHeader: PropTypes.func,
    withoutDrawerContent: PropTypes.func,
    size: PropTypes.string,
};

const component = React.memo(HyperDrawer);

component.displayName = name;

export default component;
